import React, { FC, useState } from 'react';
import Modal from 'components/shared/Modal';
import { useTranslation } from 'react-i18next';
import PostPreviewLayoutSwitch from 'app/components/PostPreviewLayoutSwitch';
import { useQuery } from 'react-query';
import { PostDetail } from 'types/PostDetail';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import { PostPreviewVariant } from '@kontentino/kontentino-post-previews';
import PostRevisionsPreview from './PostRevisionsPreview';
import { ActivityIndicator, Alert } from '@kontentino/ui';

type PostProps = {
  id: number;
  title: string;
};

type Props = {
  isShown: boolean;
  onClose: () => void;
  currentPost: PostProps;
  revisionPost: PostProps;
};

const PostRevisionsModal: FC<Props> = ({
  isShown,
  onClose,
  currentPost,
  revisionPost,
}) => {
  const { t } = useTranslation();
  const [activeVariant, setactiveVariant] =
    useState<PostPreviewVariant>('desktop');

  const { data: currentPostData } = useQuery<PostDetail>(
    queryKey.postDetail(currentPost.id),
    () => PostApi.detail(currentPost.id),
  );

  const {
    isLoading,
    isError,
    data: revisionPostData,
  } = useQuery(
    queryKey.postRevisionDetail({
      postId: currentPost.id,
      revisionId: revisionPost.id,
    }),
    () => PostApi.getPostRevisionDetail(currentPost.id, revisionPost.id),
  );

  return (
    <Modal
      open={isShown}
      focusTrapped={false}
      size="responsive"
      isContentHeightAutoResizable={false}
      dataCy={{
        wrapper: 'modal_post-revisions-view',
      }}
      onClose={onClose}
      classNames={{
        modal: '!tw-w-[712px]',
      }}
    >
      <Modal.Header title={t('comparison')} />
      <Modal.Content>
        <PostPreviewLayoutSwitch
          onVariantChange={setactiveVariant}
          activeVariant={activeVariant}
        />
        {isError && (
          <Alert variant="danger" className="tw-mt-6">
            {t('somethingWentWrong')} {t('weWereUnablToLoadPreviewOfYourPost')}
          </Alert>
        )}
        {isLoading && (
          <div className="tw-flex tw-w-full tw-justify-center">
            <ActivityIndicator className="tw-h-9 tw-w-9" />
          </div>
        )}
        <div className="tw-mb-6 tw-flex tw-justify-center tw-gap-6">
          {!isLoading && currentPostData && (
            <>
              <PostRevisionsPreview
                variant={activeVariant}
                revision={{
                  data: currentPostData,
                  label: t('current'),
                  title: currentPost.title,
                }}
              />

              <PostRevisionsPreview
                variant={activeVariant}
                revision={{
                  data: revisionPostData ?? currentPostData,
                  label: t('older'),
                  id: revisionPost.id,
                  title: revisionPost.title,
                }}
              />
            </>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default PostRevisionsModal;
